import React, { useState, useEffect } from "react";
// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDBadge from "components/MDBadge";
import DataTable from "examples/Tables/DataTable";

// MUI Components
import {
  Pagination,
  Icon,
  Switch,
  FormControlLabel,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { ToggleOn, ToggleOff } from "@mui/icons-material";

// API & Utils
import PartitionApi from "../../Classes/API/PartitionApi";
import toast from "react-hot-toast";

const PartitionList = ({ refreshKey }) => {
  const [partitions, setPartitions] = useState([]);
  const [loading, setLoading] = useState(false);

  // States للـ Pagination والبحث
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [showInactive, setShowInactive] = useState(false);

  const fetchPartitions = async () => {
    setLoading(true);
    try {
      const req = {
        name: searchTerm,
        showInactive: showInactive,
        pagingParams: {
          pageNumber: pageNumber,
          pageSize: 5,
        },
      };

      const res = await PartitionApi.GetPartitions(req);

      // التعامل مع الـ Structure الجديد: res.data مباشرة يحتوي على PagedList
      if (res.status === "Success" && res.data) {
        setPartitions(res.data.list || []);
        setTotalPages(res.data.totalPages || 1);
      }
    } catch (error) {
      console.error("Error fetching partitions:", error);
      toast.error("Failed to load partitions");
    } finally {
      setLoading(false);
    }
  };

  // إعادة التحميل عند تغيير البحث، الصفحة، أو الفلتر
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchPartitions();
    }, 400);
    return () => clearTimeout(delayDebounceFn);
  }, [refreshKey, showInactive, pageNumber, searchTerm]);

  const handleToggle = async (row) => {
    const loadId = toast.loading("Updating status...");
    try {
      const res = await PartitionApi.UpdatePartition(row.id, {
        ...row,
        isActive: !row.isActive,
      });
      if (res.status === "Success") {
        toast.success("Updated successfully", { id: loadId });
        fetchPartitions();
      } else {
        toast.error(res.message, { id: loadId });
      }
    } catch (err) {
      toast.error("Network error", { id: loadId });
    }
  };

  const updateCapacity = async (row, newCap) => {
    const val = parseInt(newCap);
    if (val === row.capacity || isNaN(val)) return;

    const loadId = toast.loading("Saving capacity...");
    try {
      const res = await PartitionApi.UpdatePartition(row.id, {
        ...row,
        capacity: val,
      });
      if (res.status === "Success") {
        toast.success("Capacity saved", { id: loadId });
        fetchPartitions();
      }
    } catch (err) {
      toast.error("Error updating capacity", { id: loadId });
    }
  };

  const columns = [
    {
      Header: "Partition Name",
      accessor: "name",
      Cell: ({ value, row }) => (
        <MDTypography
          variant="button"
          fontWeight="bold"
          sx={{ opacity: row.original.isActive ? 1 : 0.5 }}
        >
          {value || "Unnamed Partition"}
        </MDTypography>
      ),
    },
    {
      Header: "Capacity",
      accessor: "capacity",
      align: "center",
      Cell: ({ value, row }) => (
        <MDInput
          type="number"
          size="small"
          defaultValue={value}
          label="Capacity"
          onBlur={(e) => updateCapacity(row.original, e.target.value)}
          sx={{ width: "100px" }}
          disabled={!row.original.isActive}
        />
      ),
    },
    {
      Header: "Status",
      accessor: "isActive",
      Cell: ({ value }) => (
        <MDBadge
          variant="gradient"
          color={value ? "success" : "secondary"}
          size="xs"
          badgeContent={value ? "Active" : "Inactive"}
          container
        />
      ),
    },
    {
      Header: "Toggle",
      Cell: ({ row }) => (
        <IconButton
          onClick={() => handleToggle(row.original)}
          color={row.original.isActive ? "info" : "error"}
        >
          {row.original.isActive ? (
            <ToggleOn fontSize="large" />
          ) : (
            <ToggleOff fontSize="large" />
          )}
        </IconButton>
      ),
    },
  ];

  return (
    <MDBox>
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2}
        mb={1}
        gap={2}
      >
        <MDBox flexGrow={1}>
          <MDInput
            fullWidth   
            label="Search by name..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPageNumber(1);
            }}
          />
        </MDBox>

        <FormControlLabel
          sx={{ whiteSpace: "nowrap", ml: 2 }}
          control={
            <Switch
              checked={showInactive}
              onChange={(e) => {
                setShowInactive(e.target.checked);
                setPageNumber(1);
              }}
              color="info"
            />
          }
          label={
            <MDTypography variant="button" color="text">
              Show Deactivated
            </MDTypography>
          }
        />
      </MDBox>
      {/* Main Table */}
      <MDBox position="relative">
        <DataTable
          table={{ columns, rows: partitions }}
          isSorted={false}
          entriesPerPage={false}
          showTotalEntries={true}
          noEndBorder
        />

        {loading && (
          <MDBox
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
            bgcolor="rgba(255,255,255,0.6)"
            zIndex={2}
          >
            <CircularProgress color="info" />
          </MDBox>
        )}
      </MDBox>

      {/* Modern Pagination Footer */}
      <MDBox display="flex" justifyContent="center" mt={3} pb={2}>
        <Pagination
          count={totalPages}
          page={pageNumber}
          onChange={(e, value) => setPageNumber(value)}
          color="info"
          variant="outlined"
          shape="rounded"
        />
      </MDBox>
    </MDBox>
  );
};

export default PartitionList;
